/**
 * WITHDRAWAL MANAGEMENT DASHBOARD
 * 
 * Admin interface for managing user withdrawal requests from the commission_withdrawals table.
 * Allows admins to view, approve, reject, and process user withdrawal requests.
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { withdrawalEscrowService } from '../../lib/services/withdrawalEscrowService';
import { getServerSessionId, ensureSessionHeaders } from '../../lib/serverSession';
import { logAction } from '../../lib/personalActivityLogger';
import { busyAdminName, claimHeldByOther, compareWithdrawalQueue } from '../../lib/withdrawalProcessingClaim';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface WithdrawalRequest {
  id: string;
  user_id: number;
  withdrawal_amount: number;
  wallet_address: string;
  network: string;
  currency: string;
  status: string;
  created_at: string;
  updated_at: string;
  admin_notes: string | null;
  rejection_reason: string | null;
  transaction_hash: string | null;
  proof_of_payment_url: string | null;
  user_confirmed_receipt: boolean | null;
  completed_at: string | null;
  processed_at: string | null;
  // NEW: Verification tracking fields
  pin_verified: boolean;
  link_verified: boolean;
  verified_at: string | null;
  // Verification email timestamp from withdrawal_verification_pins
  verification_email_sent_at: string | null;
  users?: {
    username: string;
    email: string;
    full_name: string;
    phone?: string | null;
  };
  // Locked wallet verification (populated by API)
  _locked_wallet_address?: string | null;
  _locked_wallet_verified?: boolean | null; // null = no locked wallet, true = match, false = mismatch
  _displayed_balance?: number;
  _computed_withdrawable_balance?: number;
  _withdrawal_blocked?: boolean;
  _withdrawal_block_reason_code?: string | null;
  _withdrawal_block_reason_message?: string | null;
  processing_admin_user_id?: string | null;
  processing_admin_name?: string | null;
  processing_claimed_at?: string | null;
  processing_claim_expires_at?: string | null;
}

interface WithdrawalManagementDashboardProps {
  currentUser: any;
}

const WithdrawalManagementDashboard: React.FC<WithdrawalManagementDashboardProps> = ({ currentUser }) => {
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<WithdrawalRequest | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'approve' | 'reject' | 'view' | 'automate'>('view');
  const [transactionHash, setTransactionHash] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Fee statistics
  const [feeStats, setFeeStats] = useState<{ total_fees: number; fee_count: number }>({ total_fees: 0, fee_count: 0 });

  // Remitano Automation State
  const [automationStatus, setAutomationStatus] = useState<{
    configured: boolean;
    dryRun: boolean;
    healthy: boolean;
    message: string;
    remainingCalls: number;
    totpConfigured?: boolean;
  } | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [automationResult, setAutomationResult] = useState<{
    success: boolean;
    transactionId?: string;
    error?: string;
    errorCode?: string;
    retryable?: boolean;
    dryRun?: boolean;
  } | null>(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendResult, setResendResult] = useState<{ success: boolean; message: string } | null>(null);
  // Track which withdrawals have had emails sent in this session
  const [emailSentIds, setEmailSentIds] = useState<Set<string>>(new Set());
  // Track which withdrawal is currently sending email
  const [sendingEmailId, setSendingEmailId] = useState<string | null>(null);

  // v4.7.73 — Source-of-Funds audit state. The approve modal masks the wallet
  // address + amount until an audit has been run, and gates the Approve button
  // behind a successful (can_approve=true) audit verdict.
  const [auditData, setAuditData] = useState<any | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [auditedWithdrawalId, setAuditedWithdrawalId] = useState<string | null>(null);
  const [showFullBreakdown, setShowFullBreakdown] = useState(false);
  // Track in-flight suspend-and-reject so per-row buttons disable correctly.
  const [suspendingId, setSuspendingId] = useState<string | null>(null);

  // v4.8.14 — Typed-confirmation modal state for the destructive Suspend &
  // Reject action. Replaces the previous one-shot window.prompt flow so an
  // accidental click cannot commit a fraud-suspension. Admin must enter a
  // reason AND type one of (username | withdrawal id | exact phrase
  // "SUSPEND AND REJECT") before the POST fires.
  const [fraudConfirmTarget, setFraudConfirmTarget] = useState<WithdrawalRequest | null>(null);
  const [fraudConfirmReason, setFraudConfirmReason] = useState('');
  const [fraudConfirmTyped, setFraudConfirmTyped] = useState('');
  const [fraudConfirmError, setFraudConfirmError] = useState<string | null>(null);

  // Helper to get admin user ID
  const getAdminUserId = async (): Promise<number> => {
    let adminUserId = 1; // Default fallback
    if (currentUser?.adminUser?.email) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/withdrawals`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
          body: JSON.stringify({ operation: 'get_admin_user_id', data: { email: currentUser.adminUser.email } })
        });
        const result = await response.json();
        if (response.ok && result.data?.userId) {
          adminUserId = result.data.userId;
        }
      } catch (e) {
        console.warn('Failed to get admin user ID:', e);
      }
    }
    return adminUserId;
  };

  const releaseProcessingClaim = async (withdrawalId: string) => {
    try {
      await fetch(`${API_BASE_URL}/api/admin/withdrawals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({ operation: 'release_withdrawal_processing', data: { withdrawalId } })
      });
    } catch (error) {
      console.warn('Could not release withdrawal claim:', error);
    }
  };

  const claimProcessing = async (withdrawalId: string) => {
    const response = await fetch(`${API_BASE_URL}/api/admin/withdrawals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
      body: JSON.stringify({ operation: 'claim_withdrawal_processing', data: { withdrawalId } })
    });
    const result = await response.json();
    if (!response.ok || !result.data?.ok) {
      return { ok: false as const, error: result.error || 'Another admin is busy with this withdrawal.', name: result.processing_admin_name || null };
    }
    return { ok: true as const, claim: result.data };
  };

  // Load withdrawal requests
  const loadWithdrawals = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      console.log('📋 Loading withdrawal requests...');

      const response = await fetch(`${API_BASE_URL}/api/admin/withdrawals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({ operation: 'load_withdrawals', data: { statusFilter } })
      });
      const result = await response.json();

      if (!response.ok) {
        console.error('❌ Error loading withdrawals:', result.error);
        if (!silent) setWithdrawals([]);
        return;
      }

      const data = result.data?.withdrawals || [];
      console.log(`✅ Loaded ${data.length} withdrawal requests`);

      // Map the nested verification data to a flat field
      const mappedData = data.map((w: any) => ({
        ...w,
        verification_email_sent_at: w.withdrawal_verification_pins?.[0]?.token_link_sent_at || null
      }));
      setWithdrawals(mappedData);
      setSelectedWithdrawal(prev => {
        if (!prev) return prev;
        const fresh = mappedData.find((w: WithdrawalRequest) => w.id === prev.id);
        if (!fresh) return prev;
        return {
          ...prev,
          status: fresh.status,
          processing_admin_user_id: fresh.processing_admin_user_id,
          processing_admin_name: fresh.processing_admin_name,
          processing_claimed_at: fresh.processing_claimed_at,
          processing_claim_expires_at: fresh.processing_claim_expires_at,
        };
      });

      // Load fee statistics
      try {
        const feeResponse = await fetch(`${API_BASE_URL}/api/admin/withdrawals`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
          body: JSON.stringify({ operation: 'load_fee_stats' })
        });
        const feeResult = await feeResponse.json();
        if (feeResponse.ok && feeResult.data) {
          setFeeStats({ total_fees: feeResult.data.total_fees, fee_count: feeResult.data.fee_count });
        }
      } catch (feeError) {
        console.warn('Could not load fee statistics:', feeError);
      }

    } catch (error) {
      console.error('❌ Error in loadWithdrawals:', error);
      if (!silent) setWithdrawals([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Load data on component mount and when filter changes
  useEffect(() => {
    loadWithdrawals();
  }, [statusFilter]);

  const refreshProcessingClaims = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/withdrawals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({ operation: 'load_processing_claims' })
      });
      const result = await response.json();
      if (!response.ok) return;
      const claims = result.data?.claims || [];
      const byId = new Map(claims.map((row: WithdrawalRequest) => [row.id, row]));
      const applyClaim = (row: WithdrawalRequest) => {
        const fresh = byId.get(row.id);
        if (!fresh) return row;
        return {
          ...row,
          status: fresh.status,
          processing_admin_user_id: fresh.processing_admin_user_id ?? null,
          processing_admin_name: fresh.processing_admin_name ?? null,
          processing_claimed_at: fresh.processing_claimed_at ?? null,
          processing_claim_expires_at: fresh.processing_claim_expires_at ?? null,
        };
      };
      setWithdrawals(prev => prev.map(applyClaim));
      setSelectedWithdrawal(prev => prev ? applyClaim(prev) : prev);
    } catch (error) {
      console.warn('Could not refresh withdrawal claims:', error);
    }
  };

  // Keep the other admin's screen current while both have the queue open.
  // Claim status is a small read every 2 seconds. The full queue still reloads every 15 seconds.
  useEffect(() => {
    const claimsTimer = setInterval(() => {
      refreshProcessingClaims();
    }, 2000);
    const listTimer = setInterval(() => {
      loadWithdrawals(true);
    }, 15000);
    return () => {
      clearInterval(claimsTimer);
      clearInterval(listTimer);
    };
  }, [statusFilter]);

  // Extend the claim while the approval, rejection, or suspend window stays open.
  useEffect(() => {
    const withdrawalId = (showModal && (modalType === 'approve' || modalType === 'reject') && selectedWithdrawal?.id)
      || fraudConfirmTarget?.id
      || null;
    if (!withdrawalId) return;
    const timer = setInterval(() => {
      claimProcessing(withdrawalId).then((claimed) => {
        if (!claimed.ok) loadWithdrawals(true);
      }).catch(() => {
        loadWithdrawals(true);
      });
    }, 60000);
    return () => clearInterval(timer);
  }, [showModal, modalType, selectedWithdrawal?.id, fraudConfirmTarget?.id]);

  // v4.8.14 — Escape key dismisses the typed-confirmation fraud modal without
  // any API call. Mirrors native dialog UX. Listener is bound only while the
  // modal target is set so we do not capture global key events otherwise.
  useEffect(() => {
    if (!fraudConfirmTarget) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFraudConfirm();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fraudConfirmTarget]);

  // Load automation status on mount - calls server-side API to get real config
  useEffect(() => {
    const loadAutomationStatus = async () => {
      try {
        // Call the server-side API to get real automation status
        // Health check endpoint doesn't require authentication
        const response = await fetch('/api/admin/remitano-withdrawal?action=health', {
          method: 'GET'
        });

        if (response.ok) {
          const data = await response.json();
          setAutomationStatus({
            configured: data.configured || false,
            dryRun: data.dryRun || false,
            healthy: data.healthy || false,
            message: data.message || 'Unknown status',
            remainingCalls: 10, // Default rate limit
            totpConfigured: data.totpConfigured || false
          });
          console.log('🤖 [AUTO] Automation status from API:', data);
        } else {
          // Fallback to client-side check (will show as not configured)
          const adminUserId = await getAdminUserId();
          const status = await withdrawalEscrowService.getAutomationStatus(adminUserId);
          setAutomationStatus(status);
          console.log('🤖 [AUTO] Automation status (fallback):', status);
        }
      } catch (error) {
        console.warn('⚠️ Could not load automation status:', error);
        setAutomationStatus({
          configured: false,
          dryRun: false,
          healthy: false,
          message: 'Failed to check automation status',
          remainingCalls: 0,
          totpConfigured: false
        });
      }
    };
    loadAutomationStatus();
  }, []);

  // Handle automated approval via Remitano - calls server-side API
  const handleAutomatedApproval = async () => {
    if (!selectedWithdrawal) return;

    try {
      setProcessing(true);
      setAutomationResult(null);
      console.log(`🤖 [AUTO] Starting automated approval for ${selectedWithdrawal.id}`);

      const adminUserId = await getAdminUserId();

      // v4.8.255: this used to hard-require a SUPABASE AUTH token
      // (supabase.auth.getSession()) and throw "No authentication session found.
      // Please log in again." before the request was ever sent. Admins do NOT sign in
      // through Supabase Auth — this platform uses its own user_sessions / X-Session-Id
      // system (api/middleware/sessionAuth.js), so that session was always null and
      // EVERY payout release failed client-side. The server never read the Bearer token:
      // api/admin/remitano-withdrawal.js is guarded by withSessionAuth(requireAdmin())
      // and contains no access_token/Authorization handling at all.
      // ensureSessionHeaders() supplies the real credentials.
      const response = await fetch('/api/admin/remitano-withdrawal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await ensureSessionHeaders())
        },
        credentials: 'include',
        body: JSON.stringify({
          action: 'process',
          withdrawalId: selectedWithdrawal.id,
          adminId: adminUserId,
          twoFactorCode: twoFactorCode || undefined,
          adminNotes: 'Automated transfer via Remitano'
        })
      });

      const result = await response.json();
      console.log('🤖 [AUTO] Server response:', result);

      setAutomationResult(result);

      if (result.success) {
        alert(`✅ Automated transfer ${result.dryRun ? '(DRY RUN) ' : ''}successful!\n\nTransaction ID: ${result.transactionId || result.remitanoTransactionId || 'N/A'}`);
        setShowModal(false);
        setSelectedWithdrawal(null);
        setTwoFactorCode('');
        loadWithdrawals();
      } else {
        // Show error but keep modal open for retry
        console.error('❌ Automated transfer failed:', result.error);
      }

    } catch (error) {
      console.error('❌ Unexpected error in automated approval:', error);
      setAutomationResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setProcessing(false);
    }
  };

  // Handle resending verification email for pending_verification withdrawals
  const handleResendVerification = async () => {
    if (!selectedWithdrawal) return;

    try {
      setResendingVerification(true);
      setResendResult(null);
      console.log(`📧 [RESEND] Resending verification for withdrawal ${selectedWithdrawal.id}`);

      // v4.8.255: same dead Supabase-Auth precondition removed — see the note on the
      // payout handler above. resend-withdrawal-verification.js is
      // withSessionAuth(requireAdmin()) and never reads a Bearer token.
      const response = await fetch('/api/admin/resend-withdrawal-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await ensureSessionHeaders())
        },
        body: JSON.stringify({
          withdrawalId: selectedWithdrawal.id
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to resend verification email');
      }

      setResendResult({ success: true, message: result.data?.message || result.message });
      console.log(`✅ [RESEND] Verification email resent successfully`);

      // Update the selected withdrawal with the new timestamp
      setSelectedWithdrawal(prev => prev ? {
        ...prev,
        verification_email_sent_at: new Date().toISOString()
      } : null);

    } catch (error) {
      console.error('❌ Error resending verification:', error);
      setResendResult({
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setResendingVerification(false);
    }
  };

  // Handle approval
  const handleApprove = async () => {
    if (!selectedWithdrawal) return;

    try {
      setProcessing(true);
      console.log(`✅ Approving withdrawal ${selectedWithdrawal.id}`);

      // Check if this is a share withdrawal (different handling)
      if (selectedWithdrawal.currency === 'SHARES') {
        // Share withdrawals don't need proof of payment - just complete them directly
        const adminUserId = await getAdminUserId();
        const result = await withdrawalEscrowService.completeShareWithdrawal(
          selectedWithdrawal.id,
          adminUserId,
          `Share withdrawal completed by admin`
        );

        if (!result.success) {
          throw new Error(result.error || 'Failed to complete share withdrawal');
        }

        alert('✅ Share withdrawal completed successfully!');
        closeProcessingModal();
        loadWithdrawals();
        return;
      }

      let proofOfPaymentUrl = '';

      // Upload proof of payment - THIS IS REQUIRED FOR USDT, NOT OPTIONAL
      if (!proofFile) {
        alert('❌ ERROR: Proof of payment file is required for USDT withdrawal approval!');
        return;
      }

      // Validate file type
      console.log(`📤 Validating proof file: ${proofFile.name}, type: ${proofFile.type}, size: ${proofFile.size}`);

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(proofFile.type)) {
        alert(`❌ CRITICAL ERROR: Invalid file type detected!\n\nFile: ${proofFile.name}\nType: ${proofFile.type}\n\nYou MUST upload an IMAGE file (JPEG, PNG, GIF, WebP) or PDF.\n\n⚠️ DO NOT upload JSON files or other document types!\n\nWithdrawal approval ABORTED.`);
        return;
      }

      // Extra validation: Check file extension matches MIME type
      const fileExtension = proofFile.name.split('.').pop()?.toLowerCase();
      const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf'];
      if (!fileExtension || !validExtensions.includes(fileExtension)) {
        alert(`❌ CRITICAL ERROR: Invalid file extension!\n\nFile: ${proofFile.name}\nExtension: ${fileExtension}\n\nAllowed extensions: .jpg, .jpeg, .png, .gif, .webp, .pdf\n\nWithdrawal approval ABORTED.`);
        return;
      }

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (proofFile.size > maxSize) {
        alert(`❌ ERROR: File too large: ${(proofFile.size / 1024 / 1024).toFixed(2)}MB. Maximum size is 10MB.`);
        return;
      }

      // Clean filename to avoid issues with special characters
      const cleanFileName = proofFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `withdrawal_proof_${selectedWithdrawal.id}_${Date.now()}_${cleanFileName}`;

      console.log(`📤 Uploading proof file: ${fileName}`);
      console.log(`📤 File details - Name: ${proofFile.name}, Type: ${proofFile.type}, Size: ${proofFile.size} bytes`);

      // Convert File to base64 for server-side upload via API
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(proofFile);
      });
      console.log(`📤 Converted to base64, uploading via API...`);

      // Upload via server-side API (uses service role key securely on server)
      const sessionId = getServerSessionId();
      const uploadResponse = await fetch(`${API_BASE_URL}/api/admin/withdrawals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'upload_proof',
          data: { withdrawalId: selectedWithdrawal.id, fileBase64, fileName, contentType: proofFile.type }
        })
      });

      if (!uploadResponse.ok) {
        const errorResult = await uploadResponse.json().catch(() => ({ error: 'Upload failed' }));
        console.error('❌ Upload response error:', uploadResponse.status, errorResult);
        throw new Error(`Upload failed: ${uploadResponse.status} - ${errorResult.error || 'Unknown error'}`);
      }

      const uploadResult = await uploadResponse.json();
      console.log('✅ Upload successful:', uploadResult);

      proofOfPaymentUrl = uploadResult.data.storagePath;
      console.log('✅ File uploaded successfully:', proofOfPaymentUrl);

      // Get the admin's user ID (integer) from the users table
      const adminUserId = await getAdminUserId();
      console.log(`👤 Admin user ID: ${adminUserId} (${currentUser?.adminUser?.email})`);

      // Use the proper withdrawal escrow service for approval
      // This ensures email notifications are sent and audit trail is created
      console.log('🔄 Calling withdrawalEscrowService.approveWithdrawal...');
      const result = await withdrawalEscrowService.approveWithdrawal(
        selectedWithdrawal.id,
        adminUserId, // Use integer user ID, not UUID admin_user ID
        proofOfPaymentUrl,
        transactionHash || '',
        `Approved via admin dashboard by ${currentUser?.adminUser?.email || 'admin'}`
      );

      console.log('📊 Approval result:', result);

      if (!result.success) {
        // CRITICAL: If approval failed, we need to clean up the uploaded file
        console.error('❌ CRITICAL: Approval failed, attempting to delete uploaded file...');
        try {
          const delSessionId = getServerSessionId();
          await fetch(`${API_BASE_URL}/api/admin/withdrawals`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(delSessionId ? { 'X-Session-Id': delSessionId } : {})
            },
            body: JSON.stringify({ operation: 'delete_proof', data: { fileName: proofOfPaymentUrl } })
          });
          console.log('✅ Uploaded file deleted successfully');
        } catch (deleteError) {
          console.error('⚠️ Failed to delete uploaded file:', deleteError);
        }

        alert(`❌ CRITICAL ERROR: Withdrawal approval failed!\n\nError: ${result.error || 'Unknown error'}\n\nThe uploaded proof file has been removed. Please fix the issue and try again.`);
        return; // STOP - DO NOT SHOW SUCCESS MESSAGE
      }

      // Verify that the approval actually worked by checking the database
      const verifyResponse = await fetch(`${API_BASE_URL}/api/admin/withdrawals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({ operation: 'verify_withdrawal', data: { withdrawalId: selectedWithdrawal.id } })
      });
      const verifyResult = await verifyResponse.json();
      const verifyData = verifyResult.data;
      const verifyError = !verifyResponse.ok;

      if (verifyError || !verifyData) {
        alert('❌ CRITICAL ERROR: Could not verify withdrawal approval status!\n\nPlease check the database manually.');
        return;
      }

      if (!['approved', 'completed'].includes(verifyData.status)) {
        alert(`❌ CRITICAL ERROR: Withdrawal status is "${verifyData.status}" instead of "approved" or "completed"!\n\nSomething went wrong. Please check the database.`);
        return;
      }

      console.log('✅ Withdrawal approval verified in database:', verifyData);

      logAction('payment', 'withdrawal_approved', { target_type: 'withdrawal', target_id: String(selectedWithdrawal.id), target_label: `${selectedWithdrawal.currency} ${selectedWithdrawal.withdrawal_amount}` });
      const emailLine = result.emailNotificationFailed
        ? `⚠️ User email notification failed: ${result.warning || 'manual resend may be required'}`
        : '✅ User notified via email';
      alert(`✅ SUCCESS: Withdrawal approved successfully!\n\n✅ File uploaded\n✅ Database updated\n${emailLine}\n✅ Audit trail created`);
      closeProcessingModal();
      setTransactionHash('');
      setProofFile(null);
      loadWithdrawals();

    } catch (error) {
      console.error('❌ Error approving withdrawal:', error);

      // Provide more specific error messages
      let errorMessage = 'Failed to approve withdrawal';
      if (error instanceof Error) {
        if (error.message.includes('upload')) {
          errorMessage = 'Withdrawal approved but file upload failed. The approval was still processed successfully.';
        } else if (error.message.includes('permission')) {
          errorMessage = 'Permission denied. Please check your admin privileges.';
        } else if (error.message.includes('network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = `Failed to approve withdrawal: ${error.message}`;
        }
      }

      alert(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  // Handle rejection
  const handleReject = async () => {
    if (!selectedWithdrawal || !rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      setProcessing(true);
      console.log(`❌ Rejecting withdrawal ${selectedWithdrawal.id}`);

      const adminUserId = await getAdminUserId();

      // Use the appropriate rejection function based on currency type
      let result;
      if (selectedWithdrawal.currency === 'SHARES') {
        // Share withdrawal rejection
        result = await withdrawalEscrowService.rejectShareWithdrawal(
          selectedWithdrawal.id,
          adminUserId,
          rejectionReason
        );
      } else {
        // USDT withdrawal rejection
        result = await withdrawalEscrowService.rejectWithdrawal(
          selectedWithdrawal.id,
          adminUserId,
          rejectionReason
        );
      }

      if (!result.success) {
        throw new Error(result.error || 'Failed to reject withdrawal');
      }

      const currencyType = selectedWithdrawal.currency === 'SHARES' ? 'shares' : 'USDT + $2 fee';
      logAction('payment', 'withdrawal_rejected', { target_type: 'withdrawal', target_id: String(selectedWithdrawal.id), target_label: `${selectedWithdrawal.currency} ${selectedWithdrawal.withdrawal_amount}`, details: { reason: rejectionReason.trim() } });
      alert(`Withdrawal rejected successfully! User has been notified via email and ${currencyType} returned.`);
      closeProcessingModal();
      setRejectionReason('');
      loadWithdrawals();

    } catch (error) {
      console.error('❌ Error rejecting withdrawal:', error);
      alert('Failed to reject withdrawal: ' + (error as Error).message);
    } finally {
      setProcessing(false);
    }
  };

  const closeProcessingModal = () => {
    const current = selectedWithdrawal;
    const type = modalType;
    setShowModal(false);
    setSelectedWithdrawal(null);
    if (current && (type === 'approve' || type === 'reject')) {
      releaseProcessingClaim(current.id);
    }
  };

  const startProcessing = async (withdrawal: WithdrawalRequest, type: 'approve' | 'reject') => {
    if (claimHeldByOther(withdrawal, currentUser?.adminUser?.id)) {
      alert(`${busyAdminName(withdrawal) || 'Another admin'} is busy with this withdrawal.`);
      return;
    }
    try {
      const claimed = await claimProcessing(withdrawal.id);
      if (!claimed.ok) {
        alert(claimed.error);
        loadWithdrawals(true);
        return;
      }
      const claimedWithdrawal = {
        ...withdrawal,
        processing_admin_user_id: claimed.claim.processing_admin_user_id,
        processing_admin_name: claimed.claim.processing_admin_name,
        processing_claimed_at: claimed.claim.processing_claimed_at,
        processing_claim_expires_at: claimed.claim.processing_claim_expires_at,
      };
      setWithdrawals(prev => prev.map(row => row.id === withdrawal.id ? { ...row, ...claimedWithdrawal } : row));
      openModal(claimedWithdrawal, type);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Could not take this withdrawal');
    }
  };

  // Open modal
  const openModal = (withdrawal: WithdrawalRequest, type: 'approve' | 'reject' | 'view' | 'automate') => {
    setSelectedWithdrawal(withdrawal);
    setModalType(type);
    setShowModal(true);
    // Reset automation state when opening automation modal
    if (type === 'automate') {
      setTwoFactorCode('');
      setAutomationResult(null);
    }
    // v4.7.73 — reset Source-of-Funds audit state each time the modal opens.
    // If the same withdrawal was previously audited in this session, the
    // verdict still has to be re-rendered fresh so the admin sees the report
    // banner before the Approve button unlocks.
    setAuditData(null);
    setAuditError(null);
    setAuditLoading(false);
    setAuditedWithdrawalId(null);
    setShowFullBreakdown(false);
    // v4.7.53 — capture which user's withdrawal is being acted on
    logAction('withdrawal', `open_withdrawal_${type}`, {
      target_type: 'user',
      target_id: String(withdrawal.user_id),
      target_label: withdrawal.users?.username || withdrawal.users?.email || `User ${withdrawal.user_id}`,
      details: { withdrawal_id: withdrawal.id, amount: withdrawal.withdrawal_amount, currency: withdrawal.currency, network: withdrawal.network }
    });
  };

  // v4.7.73 — Run the Source-of-Funds audit for the currently-open withdrawal.
  // Calls api/admin/withdrawals.js::audit_withdrawal_source and stores the
  // verdict for the modal panel. The audit is read-only and idempotent.
  const handleVerifyIntegrity = async () => {
    if (!selectedWithdrawal) return;
    if (claimHeldByOther(selectedWithdrawal, currentUser?.adminUser?.id)) {
      setAuditError(`${busyAdminName(selectedWithdrawal) || 'Another admin'} is busy with this withdrawal.`);
      return;
    }
    try {
      setAuditLoading(true);
      setAuditError(null);
      setAuditData(null);
      const response = await fetch(`${API_BASE_URL}/api/admin/withdrawals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({
          operation: 'audit_withdrawal_source',
          data: { withdrawalId: selectedWithdrawal.id }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Audit failed');
      setAuditData(result.data);
      setAuditedWithdrawalId(selectedWithdrawal.id);
      logAction('payment', 'withdrawal_source_audited', {
        target_type: 'withdrawal', target_id: String(selectedWithdrawal.id),
        target_label: `${selectedWithdrawal.currency} ${selectedWithdrawal.withdrawal_amount}`,
        details: {
          can_approve: result.data?.can_approve,
          requested: result.data?.totals?.requested_amount,
          payable_legitimate: result.data?.totals?.payable_legitimate,
          phantom: result.data?.totals?.phantom
        }
      });
    } catch (e) {
      setAuditError(e instanceof Error ? e.message : 'Audit failed');
    } finally {
      setAuditLoading(false);
    }
  };

  // v4.7.73 / v4.8.14 — Suspend User + Reject Withdrawal entry point.
  // v4.8.14 replaced the previous window.prompt confirmation with a typed-
  // confirmation modal. handleSuspendAndReject is now ONLY an opener — it
  // populates the modal state and returns; no API call is made here. The
  // destructive POST is issued from executeSuspendAndReject after the admin
  // (a) enters a reason and (b) types the username, the withdrawal ID, or
  // the exact phrase "SUSPEND AND REJECT".
  const handleSuspendAndReject = async (withdrawal: WithdrawalRequest) => {
    if (claimHeldByOther(withdrawal, currentUser?.adminUser?.id)) {
      alert(`${busyAdminName(withdrawal) || 'Another admin'} is busy with this withdrawal.`);
      return;
    }
    try {
      const claimed = await claimProcessing(withdrawal.id);
      if (!claimed.ok) {
        alert(claimed.error);
        loadWithdrawals(true);
        return;
      }
      const claimedWithdrawal = {
        ...withdrawal,
        processing_admin_user_id: claimed.claim.processing_admin_user_id,
        processing_admin_name: claimed.claim.processing_admin_name,
        processing_claimed_at: claimed.claim.processing_claimed_at,
        processing_claim_expires_at: claimed.claim.processing_claim_expires_at,
      };
      setWithdrawals(prev => prev.map(row => row.id === withdrawal.id ? { ...row, ...claimedWithdrawal } : row));
      setFraudConfirmTarget(claimedWithdrawal);
      setFraudConfirmReason('');
      setFraudConfirmTyped('');
      setFraudConfirmError(null);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Could not take this withdrawal');
    }
  };

  // Close the typed-confirmation modal without firing the API. Called by the
  // Cancel button, the Escape key, and after a successful destructive POST.
  const closeFraudConfirm = () => {
    const target = fraudConfirmTarget;
    setFraudConfirmTarget(null);
    setFraudConfirmReason('');
    setFraudConfirmTyped('');
    setFraudConfirmError(null);
    if (target) releaseProcessingClaim(target.id);
  };

  // v4.8.14 — Execute the suspend-and-reject POST after typed confirmation.
  // Server-side API contract is byte-for-byte unchanged from v4.8.13: same
  // POST /api/admin/withdrawals operation 'suspend_and_reject_withdrawal',
  // same data shape ({ withdrawalId, adminId, reason }). No financial, no
  // commission, no withdrawal logic changes — this is a client-side guard
  // against accidental destructive clicks only.
  const executeSuspendAndReject = async () => {
    const withdrawal = fraudConfirmTarget;
    if (!withdrawal) return;
    const reason = fraudConfirmReason.trim();
    if (!reason) {
      setFraudConfirmError('Reason is required.');
      return;
    }
    const typed = fraudConfirmTyped.trim();
    const expectedUsername = (withdrawal.users?.username || '').trim();
    const expectedId = String(withdrawal.id).trim();
    const accepted =
      typed === 'SUSPEND AND REJECT' ||
      (expectedUsername.length > 0 && typed.toLowerCase() === expectedUsername.toLowerCase()) ||
      typed.toLowerCase() === expectedId.toLowerCase();
    if (!accepted) {
      setFraudConfirmError(
        'Confirmation does not match. Type the username, the withdrawal ID, or the exact phrase SUSPEND AND REJECT.'
      );
      return;
    }
    setFraudConfirmError(null);
    try {
      setSuspendingId(withdrawal.id);
      const adminUserId = await getAdminUserId();
      const response = await fetch(`${API_BASE_URL}/api/admin/withdrawals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({
          operation: 'suspend_and_reject_withdrawal',
          data: { withdrawalId: withdrawal.id, adminId: adminUserId, reason }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Suspend & reject failed');
      logAction('payment', 'fraud_suspend_and_reject', {
        target_type: 'user', target_id: String(withdrawal.user_id),
        target_label: withdrawal.users?.username || `User ${withdrawal.user_id}`,
        details: { withdrawal_id: withdrawal.id, reason, reject_error: result.data?.reject_error || null }
      });
      const rejectMsg = result.data?.reject_error
        ? `\n\n⚠ Note: withdrawal rejection RPC returned: ${result.data.reject_error}`
        : '';
      alert(`✅ Account suspended and withdrawal rejected.${rejectMsg}`);
      closeFraudConfirm();
      if (showModal) {
        setShowModal(false);
        setSelectedWithdrawal(null);
      }
      loadWithdrawals();
    } catch (e) {
      setFraudConfirmError(e instanceof Error ? e.message : 'Suspend & reject failed');
    } finally {
      setSuspendingId(null);
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#F59E0B';
      case 'approved': return '#10B981';
      case 'rejected': return '#EF4444';
      case 'completed': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  // Get status counts
  const statusCounts = withdrawals.reduce((acc, w) => {
    acc[w.status] = (acc[w.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const myAdminId = currentUser?.adminUser?.id || null;
  const queuedWithdrawals = [...withdrawals].sort(compareWithdrawalQueue);
  const selectedHeldByOther = selectedWithdrawal ? claimHeldByOther(selectedWithdrawal, myAdminId) : false;
  const selectedBusyName = selectedWithdrawal ? busyAdminName(selectedWithdrawal) : null;

  return (
    <div style={{ padding: '24px', backgroundColor: '#111827', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#ffffff', marginBottom: '8px' }}>
          💸 Withdrawal Management
        </h1>
        <p style={{ color: '#ffffff' }}>
          Manage user withdrawal requests - approve, reject, and track payments
        </p>
      </div>

      {/* Statistics Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#F59E0B' }}>{statusCounts.pending || 0}</div>
          <div style={{ color: '#ffffff', fontSize: '14px' }}>Pending Requests</div>
        </div>
        <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10B981' }}>{statusCounts.approved || 0}</div>
          <div style={{ color: '#ffffff', fontSize: '14px' }}>Approved</div>
        </div>
        <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#EF4444' }}>{statusCounts.rejected || 0}</div>
          <div style={{ color: '#ffffff', fontSize: '14px' }}>Rejected</div>
        </div>
        <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #374151' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#8B5CF6' }}>{statusCounts.completed || 0}</div>
          <div style={{ color: '#ffffff', fontSize: '14px' }}>Completed</div>
        </div>
        {/* Fee Statistics Card */}
        <div style={{ backgroundColor: '#1F2937', padding: '20px', borderRadius: '8px', border: '1px solid #065F46' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10B981' }}>${feeStats.total_fees.toFixed(2)}</div>
          <div style={{ color: '#ffffff', fontSize: '14px' }}>Network Fees Collected</div>
          <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>{feeStats.fee_count} withdrawals @ $2 each</div>
        </div>
        {/* Remitano Automation Status Card */}
        <div style={{
          backgroundColor: '#1F2937',
          padding: '20px',
          borderRadius: '8px',
          border: `1px solid ${automationStatus?.healthy ? '#8B5CF6' : automationStatus?.dryRun ? '#F59E0B' : '#6B7280'}`
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: automationStatus?.healthy ? '#8B5CF6' : automationStatus?.dryRun ? '#F59E0B' : '#6B7280'
          }}>
            {automationStatus?.dryRun ? '🧪 Dry Run' :
             automationStatus?.healthy ? '🤖 Connected' :
             automationStatus?.configured ? '⚠️ Issues' : '⚪ Not Configured'}
          </div>
          <div style={{ color: '#ffffff', fontSize: '14px' }}>Remitano Automation</div>
          <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
            {automationStatus?.message || 'Loading...'}
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <label style={{ color: '#ffffff', fontSize: '14px' }}>Filter by status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              backgroundColor: '#374151',
              color: '#ffffff',
              border: '1px solid #4B5563',
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '14px'
            }}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="pending_verification">Pending Verification</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <button
          onClick={loadWithdrawals}
          style={{
            backgroundColor: '#374151',
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Withdrawals Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#ffffff' }}>
          Loading withdrawal requests...
        </div>
      ) : withdrawals.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#ffffff' }}>
          No withdrawal requests found for the selected filter.
        </div>
      ) : (
        <div style={{ backgroundColor: '#1F2937', borderRadius: '8px', border: '1px solid #374151', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#374151' }}>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>User</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Amount</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Balance View</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Network</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Verified</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Confirmation</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontWeight: '500' }}>Admin</th>
                <th style={{ padding: '12px', textAlign: 'center', color: '#ffffff', fontWeight: '500' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {queuedWithdrawals.map((withdrawal) => (
                <tr key={withdrawal.id} style={{ borderBottom: '1px solid #374151' }}>
                  <td style={{ padding: '12px', color: '#ffffff' }}>
                    <div>
                      <div style={{ fontWeight: '500' }}>{withdrawal.users?.username || `User ${withdrawal.user_id}`}</div>
                      <div style={{ fontSize: '14px', color: '#ffffff' }}>ID: {withdrawal.user_id}</div>
                      {withdrawal.users?.phone ? (
                        <a
                          href={`https://wa.me/${withdrawal.users.phone.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginTop: '4px',
                            backgroundColor: '#25D36620',
                            color: '#25D366',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            textDecoration: 'none',
                            cursor: 'pointer'
                          }}
                          title={`WhatsApp: ${withdrawal.users.phone}`}
                        >
                          💬 WhatsApp
                        </a>
                      ) : (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginTop: '4px',
                            backgroundColor: '#6B728020',
                            color: '#6B7280',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'default'
                          }}
                          title="No phone number on file"
                        >
                          💬 No phone
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px', color: '#ffffff' }}>
                    <div>
                      <div style={{ fontWeight: '500' }}>${withdrawal.withdrawal_amount}</div>
                      <div style={{ fontSize: '14px', color: '#ffffff' }}>{withdrawal.currency || 'USDT'}</div>
                    </div>
                  </td>
                  <td style={{ padding: '12px', color: '#ffffff' }}>
                    <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                      <div>Displayed: ${(Number(withdrawal._displayed_balance || 0)).toFixed(2)}</div>
                      <div>Withdrawable: ${(Number(withdrawal._computed_withdrawable_balance || 0)).toFixed(2)}</div>
                      {withdrawal._withdrawal_blocked && (
                        <div style={{ color: '#F59E0B', marginTop: '2px' }}>
                          Restricted ({withdrawal._withdrawal_block_reason_code || 'OTHER'})
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px', color: '#ffffff' }}>
                    <div>{withdrawal.network || 'N/A'}</div>
                    {withdrawal._locked_wallet_verified === true ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '3px', marginTop: '4px',
                        backgroundColor: '#10B98120', color: '#10B981',
                        padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600'
                      }} title={`Matches locked wallet: ${withdrawal._locked_wallet_address}`}>
                        ✓ Wallet Verified
                      </span>
                    ) : withdrawal._locked_wallet_verified === false ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '3px', marginTop: '4px',
                        backgroundColor: '#EF444420', color: '#EF4444',
                        padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600'
                      }} title={`MISMATCH! Locked: ${withdrawal._locked_wallet_address || 'none'} vs Request: ${withdrawal.wallet_address}`}>
                        ⚠ Wallet Mismatch
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '3px', marginTop: '4px',
                        backgroundColor: '#F59E0B20', color: '#F59E0B',
                        padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600'
                      }} title="No locked wallet registered for this network">
                        ⚠ No Locked Wallet
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      backgroundColor: getStatusColor(withdrawal.status) + '20',
                      color: getStatusColor(withdrawal.status),
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      {withdrawal.status.toUpperCase()}
                    </span>
                    {(withdrawal as any)._silent_blocked && (
                      <span style={{
                        backgroundColor: '#DC262620',
                        color: '#DC2626',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '700',
                        marginLeft: '6px',
                        border: '1px solid #DC2626'
                      }}>
                        🚨 FLAGGED
                      </span>
                    )}
                  </td>
                  {/* Verified Column - shows PIN/Link verification status */}
                  <td style={{ padding: '12px' }}>
                    {withdrawal.link_verified ? (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: '#10B98120',
                        color: '#10B981',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        ✅ Link
                      </div>
                    ) : withdrawal.pin_verified ? (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: '#10B98120',
                        color: '#10B981',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        ✅ PIN
                      </div>
                    ) : withdrawal.status === 'pending_verification' ? (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: '#F59E0B20',
                        color: '#F59E0B',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        ⏳ Pending
                      </div>
                    ) : withdrawal.status === 'pending' ? (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: '#EF444420',
                        color: '#EF4444',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        ❌ Not Verified
                      </div>
                    ) : (
                      <span style={{ color: '#ffffff', fontSize: '14px' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {withdrawal.status === 'completed' && withdrawal.user_confirmed_receipt ? (
                      <div>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundColor: '#10B98120',
                          color: '#10B981',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: '500',
                          marginBottom: '4px'
                        }}>
                          ✅ User Confirmed
                        </div>
                        {withdrawal.completed_at && (
                          <div style={{ fontSize: '13px', color: '#ffffff' }}>
                            {new Date(withdrawal.completed_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                    ) : withdrawal.status === 'approved' ? (
                      <span style={{
                        backgroundColor: '#F59E0B20',
                        color: '#F59E0B',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        ⏳ Awaiting User
                      </span>
                    ) : (
                      <span style={{ color: '#ffffff', fontSize: '14px' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', color: '#ffffff', fontSize: '14px' }}>
                    {new Date(withdrawal.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px', color: '#60A5FA', fontSize: '14px', fontWeight: 600 }}>
                    {busyAdminName(withdrawal) || <span style={{ color: '#9CA3AF', fontWeight: 400 }}>—</span>}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => openModal(withdrawal, 'view')}
                        style={{
                          backgroundColor: '#374151',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        👁️ View
                      </button>
                      {/* Send Verification Email Button - for unverified pending withdrawals */}
                      {(withdrawal.status === 'pending' || withdrawal.status === 'pending_verification') && !withdrawal.pin_verified && !withdrawal.link_verified && (
                        <button
                          onClick={async () => {
                            // Don't send again if already sent or currently sending
                            if (emailSentIds.has(withdrawal.id) || sendingEmailId === withdrawal.id) return;

                            try {
                              setSendingEmailId(withdrawal.id);
                              // v4.8.255: dead Supabase-Auth precondition removed (see note
                              // on the payout handler). Admins authenticate via X-Session-Id.
                              const response = await fetch('/api/admin/resend-withdrawal-verification', {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  ...(await ensureSessionHeaders())
                                },
                                body: JSON.stringify({ withdrawalId: withdrawal.id })
                              });
                              const result = await response.json();
                              if (result.success) {
                                // Mark as sent
                                setEmailSentIds(prev => new Set(prev).add(withdrawal.id));
                                loadWithdrawals();
                              } else {
                                alert(`❌ Failed to send email: ${result.error}`);
                              }
                            } catch (error) {
                              alert(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
                            } finally {
                              setSendingEmailId(null);
                            }
                          }}
                          disabled={emailSentIds.has(withdrawal.id) || sendingEmailId === withdrawal.id}
                          style={{
                            backgroundColor: emailSentIds.has(withdrawal.id) ? '#10B981' : '#3B82F6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            cursor: emailSentIds.has(withdrawal.id) || sendingEmailId === withdrawal.id ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            opacity: sendingEmailId === withdrawal.id ? 0.7 : 1
                          }}
                          title={emailSentIds.has(withdrawal.id) ? "Verification email sent" : "Send verification email to user"}
                        >
                          {sendingEmailId === withdrawal.id ? '⏳ Sending...' : emailSentIds.has(withdrawal.id) ? '✓ Sent' : '📧 Email'}
                        </button>
                      )}
                      {withdrawal.status === 'pending' && (
                        <>
                          {/* v4.8.256: the "🤖 Auto" (Send via Remitano) button is REMOVED.
                              Remitano is fully retired (owner ruling 2026-09-16); Exodus/BSC is
                              the only rail. Exodus is a self-custody wallet with no payout API —
                              automating a send would require the signing key on the server, which
                              is exactly the exposure the 2026-03-06 breach argues against. Payouts
                              are released manually: pay from Exodus, upload the proof + tx hash,
                              then Approve. That path is handleApprove / approve_withdrawal and is
                              unaffected. The button was already invisible in production (the health
                              endpoint returns no `configured`/`dryRun`, so the guard was false) —
                              removing it makes that explicit rather than incidental. */}
                          {/* Approve Button - Only for VERIFIED withdrawals */}
                          {(withdrawal.pin_verified || withdrawal.link_verified) && !(withdrawal as any)._silent_blocked && (
                            <button
                              onClick={() => startProcessing(withdrawal, 'approve')}
                              disabled={claimHeldByOther(withdrawal, myAdminId)}
                              title={claimHeldByOther(withdrawal, myAdminId) ? `${busyAdminName(withdrawal)} is busy with this withdrawal` : 'Approve this withdrawal'}
                              style={{
                                backgroundColor: claimHeldByOther(withdrawal, myAdminId) ? '#2563EB' : '#10B981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                cursor: claimHeldByOther(withdrawal, myAdminId) ? 'not-allowed' : 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              ✅ Approve
                            </button>
                          )}
                          <button
                            onClick={() => startProcessing(withdrawal, 'reject')}
                            disabled={claimHeldByOther(withdrawal, myAdminId)}
                            title={claimHeldByOther(withdrawal, myAdminId) ? `${busyAdminName(withdrawal)} is busy with this withdrawal` : 'Reject this withdrawal'}
                            style={{
                              backgroundColor: claimHeldByOther(withdrawal, myAdminId) ? '#2563EB' : '#EF4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              cursor: claimHeldByOther(withdrawal, myAdminId) ? 'not-allowed' : 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            ❌ Reject
                          </button>
                          {/* v4.7.73 — One-click fraud mitigation: suspends the user and rejects the
                              withdrawal in a single confirm-and-go flow. Mirrors the 2026-05-17 manual sweep. */}
                          <button
                            onClick={() => handleSuspendAndReject(withdrawal)}
                            disabled={suspendingId === withdrawal.id || claimHeldByOther(withdrawal, myAdminId)}
                            title={claimHeldByOther(withdrawal, myAdminId) ? `${busyAdminName(withdrawal)} is busy with this withdrawal` : "Suspend the user's account AND reject this withdrawal (fraud response)"}
                            style={{
                              backgroundColor: claimHeldByOther(withdrawal, myAdminId) ? '#2563EB' : '#7F1D1D',
                              color: 'white',
                              border: claimHeldByOther(withdrawal, myAdminId) ? 'none' : '1px solid #EF4444',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              cursor: suspendingId === withdrawal.id || claimHeldByOther(withdrawal, myAdminId) ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              opacity: suspendingId === withdrawal.id ? 0.6 : 1
                            }}
                          >
                            {suspendingId === withdrawal.id ? '⏳ Suspending...' : '🚨 Suspend & Reject'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal for viewing/approving/rejecting withdrawals */}
      {showModal && selectedWithdrawal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1F2937',
            borderRadius: '8px',
            border: '1px solid #374151',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ color: '#ffffff', marginBottom: '16px', fontSize: '20px' }}>
              {modalType === 'approve' ? '✅ Approve Withdrawal' :
               modalType === 'reject' ? '❌ Reject Withdrawal' :
               modalType === 'automate' ? '🤖 Automated Transfer via Remitano' :
               '👁️ View Withdrawal Details'}
            </h3>

            {/* Withdrawal Details */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ color: '#ffffff', fontSize: '14px' }}>User:</label>
                  <div style={{ color: '#ffffff', fontWeight: '500' }}>{selectedWithdrawal.users?.username || `User ${selectedWithdrawal.user_id}`}</div>
                  {selectedWithdrawal.users?.phone ? (
                    <a
                      href={`https://wa.me/${selectedWithdrawal.users.phone.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginTop: '6px',
                        backgroundColor: '#25D366',
                        color: '#ffffff',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        textDecoration: 'none',
                        cursor: 'pointer'
                      }}
                      title={`Contact via WhatsApp: ${selectedWithdrawal.users.phone}`}
                    >
                      💬 Contact via WhatsApp
                    </a>
                  ) : (
                    <span style={{ display: 'block', marginTop: '6px', color: '#6B7280', fontSize: '12px' }}>
                      No phone number on file
                    </span>
                  )}
                </div>
                <div>
                  <label style={{ color: '#ffffff', fontSize: '14px' }}>Amount:</label>
                  {/* v4.7.73 — Mask amount in approve mode until Source-of-Funds audit passes. */}
                  {modalType === 'approve' && (!auditData || auditedWithdrawalId !== selectedWithdrawal.id) ? (
                    <div style={{ color: '#9CA3AF', fontWeight: '500', fontFamily: 'monospace', letterSpacing: '2px' }}>
                      $••••••
                      <span style={{ display: 'block', color: '#F59E0B', fontSize: '12px', marginTop: '4px', letterSpacing: 0 }}>
                        🔒 Hidden until integrity audit passes
                      </span>
                    </div>
                  ) : (
                    <div style={{ color: '#ffffff', fontWeight: '500' }}>${selectedWithdrawal.withdrawal_amount}</div>
                  )}
                </div>
                <div>
                  <label style={{ color: '#ffffff', fontSize: '14px' }}>Network:</label>
                  <div style={{ color: '#ffffff' }}>{selectedWithdrawal.network}</div>
                </div>
                <div>
                  <label style={{ color: '#ffffff', fontSize: '14px' }}>Status:</label>
                  <div style={{ color: getStatusColor(selectedWithdrawal.status) }}>{selectedWithdrawal.status}</div>
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: '#ffffff', fontSize: '14px' }}>Wallet Address:</label>
                {/* v4.7.73 — Mask wallet address in approve mode until audit passes. */}
                {modalType === 'approve' && (!auditData || auditedWithdrawalId !== selectedWithdrawal.id) ? (
                  <div style={{ color: '#9CA3AF', fontFamily: 'monospace', fontSize: '14px', letterSpacing: '2px' }}>
                    ••••••••••••••••••••••••••••••••
                    <span style={{ display: 'block', color: '#F59E0B', fontSize: '12px', marginTop: '4px', letterSpacing: 0, fontFamily: 'inherit' }}>
                      🔒 Hidden until integrity audit passes — prevents premature payout
                    </span>
                  </div>
                ) : (
                  <div style={{ color: '#ffffff', fontFamily: 'monospace', fontSize: '14px', wordBreak: 'break-all' }}>
                    {selectedWithdrawal.wallet_address}
                  </div>
                )}
              </div>

              {/* Locked Wallet Verification Status */}
              <div style={{
                marginBottom: '16px',
                padding: '10px 12px',
                backgroundColor: '#0F172A',
                border: '1px solid #334155',
                borderRadius: '6px'
              }}>
                <div style={{ color: '#CBD5E1', fontSize: '13px', marginBottom: '4px' }}>Withdrawal Transparency</div>
                <div style={{ color: '#ffffff', fontSize: '14px' }}>
                  Displayed Balance: ${(Number(selectedWithdrawal._displayed_balance || 0)).toFixed(2)}
                </div>
                <div style={{ color: '#ffffff', fontSize: '14px' }}>
                  Computed Withdrawable: ${(Number(selectedWithdrawal._computed_withdrawable_balance || 0)).toFixed(2)}
                </div>
                <div style={{ color: selectedWithdrawal._withdrawal_blocked ? '#F59E0B' : '#10B981', fontSize: '13px', marginTop: '4px' }}>
                  {selectedWithdrawal._withdrawal_blocked ? `Restricted (${selectedWithdrawal._withdrawal_block_reason_code || 'OTHER'})` : 'Not currently restricted'}
                </div>
                {selectedWithdrawal._withdrawal_block_reason_message && (
                  <div style={{ color: '#94A3B8', fontSize: '12px', marginTop: '4px' }}>
                    {selectedWithdrawal._withdrawal_block_reason_message}
                  </div>
                )}
              </div>

              {/* Locked Wallet Verification Status */}
              <div style={{
                marginBottom: '16px',
                padding: '10px 12px',
                backgroundColor: selectedWithdrawal._locked_wallet_verified === true ? '#10B98115' :
                                 selectedWithdrawal._locked_wallet_verified === false ? '#EF444415' : '#F59E0B15',
                border: `1px solid ${selectedWithdrawal._locked_wallet_verified === true ? '#10B981' :
                                     selectedWithdrawal._locked_wallet_verified === false ? '#EF4444' : '#F59E0B'}`,
                borderRadius: '6px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>
                    {selectedWithdrawal._locked_wallet_verified === true ? '🔒' :
                     selectedWithdrawal._locked_wallet_verified === false ? '🚨' : '⚠️'}
                  </span>
                  <div>
                    <div style={{
                      color: selectedWithdrawal._locked_wallet_verified === true ? '#10B981' :
                             selectedWithdrawal._locked_wallet_verified === false ? '#EF4444' : '#F59E0B',
                      fontWeight: '600', fontSize: '14px'
                    }}>
                      {selectedWithdrawal._locked_wallet_verified === true
                        ? 'Wallet Address Verified — Matches Locked Wallet'
                        : selectedWithdrawal._locked_wallet_verified === false
                        ? '⚠ WALLET MISMATCH — Does NOT Match Locked Wallet!'
                        : 'No Locked Wallet Registered for This Network'}
                    </div>
                    {selectedWithdrawal._locked_wallet_address && selectedWithdrawal._locked_wallet_verified === false && (
                      <div style={{ color: '#ffffff', fontSize: '12px', marginTop: '4px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        Locked: {selectedWithdrawal._locked_wallet_address}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Email Verification Status */}
              <div style={{
                marginTop: '16px',
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: (selectedWithdrawal.pin_verified || selectedWithdrawal.link_verified) ? '#10B98110' : '#F59E0B10',
                border: `1px solid ${(selectedWithdrawal.pin_verified || selectedWithdrawal.link_verified) ? '#10B981' : '#F59E0B'}`,
                borderRadius: '6px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>
                      {selectedWithdrawal.link_verified ? '🔗' : selectedWithdrawal.pin_verified ? '🔐' : '⏳'}
                    </span>
                    <div>
                      <div style={{
                        color: (selectedWithdrawal.pin_verified || selectedWithdrawal.link_verified) ? '#10B981' : '#F59E0B',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}>
                        {selectedWithdrawal.link_verified
                          ? 'Verified via Email Link'
                          : selectedWithdrawal.pin_verified
                          ? 'Verified via PIN'
                          : 'Awaiting Email Verification'}
                      </div>
                      {selectedWithdrawal.verified_at && (
                        <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
                          Verified at: {new Date(selectedWithdrawal.verified_at).toLocaleString()}
                        </div>
                      )}
                      {/* Show when verification email was last sent */}
                      {selectedWithdrawal.verification_email_sent_at && !selectedWithdrawal.pin_verified && !selectedWithdrawal.link_verified && (
                        <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
                          📧 Last email sent: {new Date(selectedWithdrawal.verification_email_sent_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Send/Resend Verification Button - for pending or pending_verification when not verified */}
                  {(selectedWithdrawal.status === 'pending_verification' || selectedWithdrawal.status === 'pending') && !selectedWithdrawal.pin_verified && !selectedWithdrawal.link_verified && (
                    <button
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      style={{
                        backgroundColor: '#3B82F6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '6px 12px',
                        cursor: resendingVerification ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: resendingVerification ? 0.7 : 1
                      }}
                    >
                      {resendingVerification ? '📧 Sending...' : selectedWithdrawal.verification_email_sent_at ? '📧 Resend Email' : '📧 Send Verification Email'}
                    </button>
                  )}
                </div>
                {/* Resend Result Message */}
                {resendResult && (
                  <div style={{
                    marginTop: '10px',
                    padding: '8px',
                    backgroundColor: resendResult.success ? '#10B98120' : '#EF444420',
                    borderRadius: '4px',
                    color: resendResult.success ? '#10B981' : '#EF4444',
                    fontSize: '14px'
                  }}>
                    {resendResult.success ? '✅' : '❌'} {resendResult.message}
                  </div>
                )}
              </div>

              {/* User Confirmation Status */}
              {(selectedWithdrawal.status === 'approved' || selectedWithdrawal.status === 'completed') && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: selectedWithdrawal.user_confirmed_receipt ? '#10B98110' : '#F59E0B10',
                  border: `1px solid ${selectedWithdrawal.user_confirmed_receipt ? '#10B981' : '#F59E0B'}`,
                  borderRadius: '6px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <span style={{ fontSize: '20px' }}>
                      {selectedWithdrawal.user_confirmed_receipt ? '✅' : '⏳'}
                    </span>
                    <div>
                      <div style={{
                        color: selectedWithdrawal.user_confirmed_receipt ? '#10B981' : '#F59E0B',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}>
                        {selectedWithdrawal.user_confirmed_receipt
                          ? 'User Confirmed Receipt'
                          : 'Awaiting User Confirmation'}
                      </div>
                      {selectedWithdrawal.user_confirmed_receipt && selectedWithdrawal.completed_at && (
                        <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
                          Confirmed on: {new Date(selectedWithdrawal.completed_at).toLocaleString()}
                        </div>
                      )}
                      {!selectedWithdrawal.user_confirmed_receipt && selectedWithdrawal.processed_at && (
                        <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
                          Approved on: {new Date(selectedWithdrawal.processed_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                  {!selectedWithdrawal.user_confirmed_receipt && (
                    <div style={{
                      color: '#ffffff',
                      fontSize: '14px',
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid #374151'
                    }}>
                      💡 The user has been notified via email and must confirm receipt of funds in their dashboard.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* v4.7.73 — Source-of-Funds verification gate. Sits at the top of
                the approve modal and must be passed before the wallet/amount
                are revealed and before the Approve button is enabled. */}
            {modalType === 'approve' && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: auditData?.can_approve ? '#065F4620' : auditData ? '#7F1D1D30' : '#1F2937',
                border: `2px solid ${auditData?.can_approve ? '#10B981' : auditData ? '#EF4444' : '#F59E0B'}`,
                borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <div style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600' }}>
                      🛡️ Source-of-Funds Audit
                    </div>
                    <div style={{ color: '#9CA3AF', fontSize: '13px', marginTop: '2px' }}>
                      Verifies commissions are linked to legitimate share purchases before payout.
                    </div>
                  </div>
                  <button
                    onClick={handleVerifyIntegrity}
                    disabled={auditLoading || selectedHeldByOther}
                    title={selectedHeldByOther ? `${selectedBusyName} is busy with this withdrawal` : 'Verify commission integrity'}
                    style={{
                      backgroundColor: selectedHeldByOther ? '#2563EB' : auditData?.can_approve ? '#10B981' : '#D4AF37',
                      color: selectedHeldByOther || auditData?.can_approve ? 'white' : '#1F2937',
                      border: 'none', borderRadius: '6px', padding: '10px 16px',
                      cursor: auditLoading || selectedHeldByOther ? 'not-allowed' : 'pointer',
                      fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap',
                      opacity: auditLoading ? 0.6 : 1
                    }}
                  >
                    {selectedHeldByOther ? 'Busy' : auditLoading ? '⏳ Auditing...' : auditData ? '🔁 Re-Run Audit' : '🔍 Verify Commission Integrity'}
                  </button>
                </div>
                {auditError && (
                  <div style={{ padding: '10px 12px', backgroundColor: '#7F1D1D', color: '#FCA5A5', borderRadius: '6px', fontSize: '13px' }}>
                    ❌ Audit failed: {auditError}
                  </div>
                )}
                {selectedHeldByOther && (
                  <div style={{ padding: '10px 12px', backgroundColor: '#1E3A8A', color: '#DBEAFE', borderRadius: '6px', fontSize: '13px', marginBottom: '8px' }}>
                    {selectedBusyName || 'Another admin'} is busy with this withdrawal.
                  </div>
                )}
                {!auditData && !auditLoading && !auditError && (
                  <div style={{ padding: '10px 12px', backgroundColor: '#1F2937', color: '#F59E0B', borderRadius: '6px', fontSize: '13px', border: '1px solid #F59E0B40' }}>
                    ⚠ You must verify commission integrity before this withdrawal can be approved.
                    The wallet address and amount remain hidden until the audit succeeds.
                  </div>
                )}
                {auditData && (
                  <div>
                    <div style={{
                      padding: '12px',
                      backgroundColor: auditData.can_approve ? '#065F46' : '#7F1D1D',
                      color: 'white', borderRadius: '6px', fontSize: '14px', marginBottom: '12px'
                    }}>
                      <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '4px' }}>
                        {auditData.can_approve
                          ? '✅ APPROVED FOR PAYOUT — commissions are legitimately earned'
                          : `❌ BLOCKED — requested amount exceeds verified earnings by $${(auditData.totals.deficit ?? 0).toFixed(2)}`}
                      </div>
                      <div style={{ fontSize: '13px', opacity: 0.9 }}>
                        {auditData.can_approve
                          ? 'You may proceed with the approval. The wallet address and amount have been revealed.'
                          : 'Approval is server-blocked. Consider Suspend & Reject if the commission balance was fraudulently inflated.'}
                      </div>
                    </div>
                    {/* v4.7.75 — 4-bucket stat grid (organic / pool / admin / phantom)
                        replaces the v4.7.74 3-bucket layout. The "Pool & Competition"
                        card breaks out platform-distributed earnings (Gold Diggers
                        Pool, competition prizes, performance pool claims) from manual
                        admin adjustments so the reviewer can immediately see which
                        portion of the payable balance came from each source. The
                        "Other Pending Escrow" line is rendered conditionally when
                        the user has additional pending withdrawals beyond this one. */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', fontSize: '13px', marginBottom: '12px' }}>
                      <div style={{ padding: '10px', backgroundColor: '#1F2937', borderRadius: '6px', border: '1px solid #374151' }}>
                        <div style={{ color: '#9CA3AF', fontSize: '11px', textTransform: 'uppercase' }}>Requested</div>
                        <div style={{ color: '#ffffff', fontWeight: '600', fontSize: '16px' }}>${(auditData.totals.requested_amount ?? 0).toFixed(2)}</div>
                      </div>
                      <div style={{ padding: '10px', backgroundColor: '#1F2937', borderRadius: '6px', border: '1px solid #10B98140' }}>
                        <div style={{ color: '#9CA3AF', fontSize: '11px', textTransform: 'uppercase' }}>Payable (Legitimate − Withdrawn − Escrow)</div>
                        <div style={{ color: '#10B981', fontWeight: '600', fontSize: '16px' }}>${(auditData.totals.payable_legitimate ?? 0).toFixed(2)}</div>
                        {(auditData.totals.other_pending_escrow ?? 0) > 0 && (
                          <div style={{ color: '#FBBF24', fontSize: '11px', marginTop: '2px' }}>
                            − ${(auditData.totals.other_pending_escrow ?? 0).toFixed(2)} in other pending withdrawals
                          </div>
                        )}
                        {/* v4.7.76 — share-conversion deduction line. Rendered when
                            commissions have been moved out of usdt_balance (typically
                            via commission_conversions to shares) and explains why the
                            payable cap is below the user's lifetime legitimate total. */}
                        {auditData.totals.balance_clamp_active && (auditData.totals.share_conversion_deduction ?? 0) > 0 && (
                          <div style={{ color: '#FBBF24', fontSize: '11px', marginTop: '2px' }}>
                            − ${(auditData.totals.share_conversion_deduction ?? 0).toFixed(2)} already converted to shares
                          </div>
                        )}
                        {auditData.totals.balance_clamp_active && (
                          <div style={{ color: '#9CA3AF', fontSize: '10px', marginTop: '2px', fontStyle: 'italic' }}>
                            capped at usdt_balance + requested (${(auditData.totals.payable_balance_cap ?? 0).toFixed(2)})
                          </div>
                        )}
                      </div>
                      <div style={{ padding: '10px', backgroundColor: '#1F2937', borderRadius: '6px', border: '1px solid #10B98140' }}>
                        <div style={{ color: '#9CA3AF', fontSize: '11px', textTransform: 'uppercase' }}>🟢 Organic (Referral Earnings)</div>
                        <div style={{ color: '#10B981', fontWeight: '600' }}>${(auditData.totals.organic_verified ?? 0).toFixed(2)}</div>
                      </div>
                      <div style={{ padding: '10px', backgroundColor: '#1F2937', borderRadius: '6px', border: '1px solid #A78BFA40' }}>
                        <div style={{ color: '#9CA3AF', fontSize: '11px', textTransform: 'uppercase' }}>🏆 Pool & Competition</div>
                        <div style={{ color: '#A78BFA', fontWeight: '600' }}>${(auditData.totals.pool_competition ?? 0).toFixed(2)}</div>
                      </div>
                      <div style={{ padding: '10px', backgroundColor: '#1F2937', borderRadius: '6px', border: '1px solid #3B82F640' }}>
                        <div style={{ color: '#9CA3AF', fontSize: '11px', textTransform: 'uppercase' }}>🔵 Manual Admin Credits</div>
                        <div style={{ color: '#3B82F6', fontWeight: '600' }}>${(auditData.totals.admin_adjustments ?? 0).toFixed(2)}</div>
                        {/* v4.7.74 — show the master-admin-authorized subtotal as a second
                            line so the reviewer can see at a glance how much of the admin
                            credits carry an explicit operator signature in admin_notes. */}
                        {(auditData.totals.admin_authorized ?? 0) > 0 && (
                          <div style={{ color: '#93C5FD', fontSize: '11px', marginTop: '2px' }}>
                            ✓ ${(auditData.totals.admin_authorized ?? 0).toFixed(2)} master-admin authorized
                          </div>
                        )}
                      </div>
                      <div style={{ padding: '10px', backgroundColor: '#1F2937', borderRadius: '6px', border: `1px solid ${auditData.totals.phantom > 0 ? '#EF4444' : '#37415160'}` }}>
                        <div style={{ color: '#9CA3AF', fontSize: '11px', textTransform: 'uppercase' }}>🚨 Phantom (Unverified)</div>
                        <div style={{ color: auditData.totals.phantom > 0 ? '#EF4444' : '#9CA3AF', fontWeight: '600' }}>
                          ${(auditData.totals.phantom ?? 0).toFixed(2)}
                        </div>
                      </div>
                      <div style={{ padding: '10px', backgroundColor: '#1F2937', borderRadius: '6px', border: '1px solid #37415160' }}>
                        <div style={{ color: '#9CA3AF', fontSize: '11px', textTransform: 'uppercase' }}>Already Withdrawn</div>
                        <div style={{ color: '#ffffff', fontWeight: '600' }}>${(auditData.balance?.total_withdrawn ?? 0).toFixed(2)}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowFullBreakdown(v => !v)}
                      style={{ background: 'transparent', color: '#9CA3AF', border: '1px solid #374151', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      {showFullBreakdown ? '▼ Hide commission breakdown' : `▶ Show commission breakdown (${auditData.breakdown?.length || 0} rows)`}
                    </button>
                    {showFullBreakdown && (
                      <div style={{ marginTop: '10px', maxHeight: '320px', overflowY: 'auto', border: '1px solid #374151', borderRadius: '6px' }}>
                        <table style={{ width: '100%', fontSize: '12px', color: '#ffffff', borderCollapse: 'collapse' }}>
                          <thead style={{ position: 'sticky', top: 0, backgroundColor: '#111827', zIndex: 1 }}>
                            <tr>
                              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #374151' }}>Date</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #374151' }}>Category</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #374151' }}>Type</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #374151' }}>Referred / Source</th>
                              <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #374151' }}>Purchase</th>
                              <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #374151' }}>Commission</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #374151', minWidth: '220px' }}>Reason / Admin Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(auditData.breakdown || []).map((row: any) => {
                              // v4.7.75 — 4-bucket rendering. The per-row colour, badge
                              // emoji and category text are derived from row.category,
                              // and the Type column now renders the backend-supplied
                              // type_label (e.g. 'Pool Payout', 'Competition Prize',
                              // 'Referral Commission') with the raw transaction_type
                              // displayed as a monospace subtitle for transparency.
                              const cat = row.category as 'organic' | 'pool' | 'admin' | 'phantom';
                              const isAdmin = cat === 'admin';
                              const isPool = cat === 'pool';
                              const isAuthorized = !!row.admin_authorized;
                              const catStyle: Record<string, { bg: string; text: string; emoji: string; label: string }> = {
                                organic: { bg: 'transparent', text: '#10B981', emoji: '🟢', label: 'organic' },
                                pool: { bg: '#3730A320', text: '#A78BFA', emoji: '🏆', label: 'pool' },
                                admin: { bg: isAuthorized ? '#1E3A8A20' : '#3B82F610', text: '#3B82F6', emoji: '🔵', label: 'admin' },
                                phantom: { bg: '#7F1D1D20', text: '#EF4444', emoji: '🚨', label: 'phantom' }
                              };
                              const s = catStyle[cat] || catStyle.phantom;
                              return (
                                <tr key={row.commission_id} style={{ backgroundColor: s.bg }}>
                                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #1F2937', whiteSpace: 'nowrap' }}>{new Date(row.created_at).toLocaleDateString()}</td>
                                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #1F2937', color: s.text, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    {s.emoji} {s.label}
                                    {/* v4.7.76 — multi-level depth badge for MLC ledger
                                        rows so the reviewer can see at a glance whether
                                        the commission came from a direct (L1) or deeper
                                        downline. CT rows have no level data so the badge
                                        only renders on ledger_source==='mlc' rows. */}
                                    {row.ledger_source === 'mlc' && row.referral_level && (
                                      <span style={{ marginLeft: '6px', padding: '1px 5px', backgroundColor: '#0EA5E920', color: '#7DD3FC', borderRadius: '3px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px' }}>
                                        L{row.referral_level}
                                      </span>
                                    )}
                                  </td>
                                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #1F2937', color: '#D1D5DB', fontSize: '11px' }}>
                                    <div style={{ color: '#E5E7EB', fontWeight: 500 }}>{row.type_label || row.transaction_type || '—'}</div>
                                    {row.transaction_type && row.transaction_type !== (row.type_label || '').toLowerCase() && (
                                      <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#6B7280', marginTop: '1px' }}>{row.transaction_type}</div>
                                    )}
                                    {/* v4.7.76 — raw multi_level_commissions.commission_type
                                        (e.g. 'LEVEL_1_USDT_AND_SHARES') as a tertiary line so
                                        the reviewer can see the original ledger label without
                                        leaving the audit panel. */}
                                    {row.raw_commission_type && (
                                      <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#6B7280', marginTop: '1px' }}>{row.raw_commission_type}</div>
                                    )}
                                  </td>
                                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #1F2937' }}>{row.referred_username || (row.referred_user_id ? `#${row.referred_user_id}` : '—')}</td>
                                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #1F2937', textAlign: 'right' }}>
                                    {row.share_purchase_total ? `$${row.share_purchase_total.toFixed(2)}` : '—'}
                                  </td>
                                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #1F2937', textAlign: 'right', fontWeight: 600 }}>${row.usdt_commission.toFixed(2)}</td>
                                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #1F2937' }}>
                                    {/* v4.7.74 — surface admin_notes prominently for admin rows + master-admin-auth badge.
                                        For phantom rows, the synthetic category_reason explains why the row was blocked. */}
                                    {isAdmin && isAuthorized && (
                                      <div style={{
                                        display: 'inline-block',
                                        marginBottom: '4px',
                                        padding: '2px 6px',
                                        backgroundColor: '#1E40AF',
                                        color: '#DBEAFE',
                                        borderRadius: '3px',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase'
                                      }}>
                                        ✓ Master Admin Authorized
                                      </div>
                                    )}
                                    {isAdmin && !isAuthorized && row.admin_notes && (
                                      <div style={{
                                        display: 'inline-block',
                                        marginBottom: '4px',
                                        padding: '2px 6px',
                                        backgroundColor: '#3B82F640',
                                        color: '#93C5FD',
                                        borderRadius: '3px',
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase'
                                      }}>
                                        Admin Adjustment
                                      </div>
                                    )}
                                    {isAdmin && !row.admin_notes && (
                                      <div style={{
                                        display: 'inline-block',
                                        marginBottom: '4px',
                                        padding: '2px 6px',
                                        backgroundColor: '#F59E0B40',
                                        color: '#FCD34D',
                                        borderRadius: '3px',
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase'
                                      }}>
                                        ⚠ Admin Type, No Note
                                      </div>
                                    )}
                                    {/* v4.7.75 — distinct badge for platform pool/competition
                                        rows so they are never confused with manual admin
                                        adjustments. The label mirrors row.type_label so the
                                        reviewer instantly sees 'Pool Payout' vs 'Competition
                                        Prize' instead of a generic 'Admin Adjustment' chip. */}
                                    {isPool && (
                                      <div style={{
                                        display: 'inline-block',
                                        marginBottom: '4px',
                                        padding: '2px 6px',
                                        backgroundColor: '#5B21B6',
                                        color: '#EDE9FE',
                                        borderRadius: '3px',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase'
                                      }}>
                                        🏆 {row.type_label || 'Pool / Competition'}
                                      </div>
                                    )}
                                    <div style={{
                                      color: (isAdmin || isPool) && row.admin_notes ? '#E5E7EB' : '#9CA3AF',
                                      fontStyle: row.admin_notes ? 'normal' : 'italic',
                                      fontWeight: isAdmin && isAuthorized ? 500 : 400,
                                      wordBreak: 'break-word'
                                    }}>
                                      {row.admin_notes || row.category_reason || row.reason || '—'}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action-specific content */}
            {modalType === 'approve' && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: '#ffffff', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    Transaction Hash (optional):
                  </label>
                  <input
                    type="text"
                    value={transactionHash}
                    onChange={(e) => setTransactionHash(e.target.value)}
                    placeholder="Enter transaction hash or payment reference"
                    style={{
                      width: '100%',
                      backgroundColor: '#374151',
                      color: '#ffffff',
                      border: '1px solid #4B5563',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ color: '#ffffff', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    Proof of Payment (REQUIRED):
                  </label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      if (file) {
                        console.log(`📁 File selected: ${file.name}, type: ${file.type}, size: ${file.size}`);
                        // Validate immediately on selection
                        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
                        if (!allowedTypes.includes(file.type)) {
                          alert(`❌ Invalid file type: ${file.type}\n\nPlease select an IMAGE file (JPEG, PNG, GIF, WebP) or PDF only!`);
                          e.target.value = ''; // Clear the input
                          setProofFile(null);
                          return;
                        }
                      }
                      setProofFile(file);
                    }}
                    required
                    style={{
                      width: '100%',
                      backgroundColor: '#374151',
                      color: '#ffffff',
                      border: '2px solid #D4AF37',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      fontSize: '14px'
                    }}
                  />
                  <div style={{ color: '#D4AF37', fontSize: '14px', marginTop: '4px' }}>
                    ⚠️ REQUIRED: Upload proof of payment (image or PDF only)
                  </div>
                  {proofFile && (
                    <div style={{ color: '#10B981', fontSize: '14px', marginTop: '4px' }}>
                      ✅ Selected: {proofFile.name} ({(proofFile.size / 1024).toFixed(2)} KB)
                    </div>
                  )}
                </div>
              </div>
            )}

            {modalType === 'reject' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#ffffff', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Rejection Reason *:
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                  rows={4}
                  style={{
                    width: '100%',
                    backgroundColor: '#374151',
                    color: '#ffffff',
                    border: '1px solid #4B5563',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>
            )}

            {/* Automated Transfer Form */}
            {modalType === 'automate' && (
              <div style={{ marginBottom: '20px' }}>
                {/* Automation Status Banner */}
                <div style={{
                  backgroundColor: automationStatus?.dryRun ? '#FEF3C7' :
                                   automationStatus?.healthy ? '#D1FAE5' : '#FEE2E2',
                  color: automationStatus?.dryRun ? '#92400E' :
                         automationStatus?.healthy ? '#065F46' : '#991B1B',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  fontSize: '14px'
                }}>
                  <strong>
                    {automationStatus?.dryRun ? '🧪 DRY RUN MODE' :
                     automationStatus?.healthy ? '✅ Remitano Connected' : '⚠️ Connection Issue'}
                  </strong>
                  <div style={{ marginTop: '4px', fontSize: '13px' }}>
                    {automationStatus?.message}
                  </div>
                  {automationStatus && (
                    <div style={{ marginTop: '4px', fontSize: '14px', opacity: 0.8 }}>
                      API calls remaining: {automationStatus.remainingCalls}
                    </div>
                  )}
                </div>

                {/* 2FA Code Input - Only show if TOTP is NOT configured on server */}
                {!automationStatus?.totpConfigured && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ color: '#ffffff', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                      🔐 Remitano 2FA Code (from your authenticator app):
                    </label>
                    <input
                      type="text"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      style={{
                        width: '200px',
                        backgroundColor: '#374151',
                        color: '#ffffff',
                        border: '2px solid #8B5CF6',
                        borderRadius: '6px',
                        padding: '12px 16px',
                        fontSize: '24px',
                        letterSpacing: '8px',
                        textAlign: 'center',
                        fontFamily: 'monospace'
                      }}
                    />
                    <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
                      Open your authenticator app and enter the current code for Remitano
                    </div>
                  </div>
                )}

                {/* Show message when TOTP is auto-configured */}
                {automationStatus?.totpConfigured && (
                  <div style={{
                    backgroundColor: '#065F46',
                    color: '#D1FAE5',
                    padding: '12px 16px',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    fontSize: '14px'
                  }}>
                    <strong>🔐 2FA Auto-Configured</strong>
                    <div style={{ marginTop: '4px', fontSize: '13px' }}>
                      2FA codes will be generated automatically - no manual entry required
                    </div>
                  </div>
                )}

                {/* Transfer Summary */}
                <div style={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '8px' }}>TRANSFER DETAILS</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div style={{ color: '#ffffff', fontSize: '13px' }}>Amount:</div>
                    <div style={{ color: '#10B981', fontSize: '13px', fontWeight: 'bold' }}>
                      ${selectedWithdrawal.withdrawal_amount.toFixed(2)} USDT
                    </div>
                    <div style={{ color: '#ffffff', fontSize: '13px' }}>Network:</div>
                    <div style={{ color: '#ffffff', fontSize: '13px' }}>{selectedWithdrawal.network}</div>
                    <div style={{ color: '#ffffff', fontSize: '13px' }}>Wallet:</div>
                    <div style={{ color: '#ffffff', fontSize: '13px', wordBreak: 'break-all' }}>
                      {selectedWithdrawal.wallet_address}
                    </div>
                  </div>
                </div>

                {/* Result Display */}
                {automationResult && (
                  <div style={{
                    backgroundColor: automationResult.success ? '#D1FAE5' : '#FEE2E2',
                    color: automationResult.success ? '#065F46' : '#991B1B',
                    padding: '12px 16px',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    fontSize: '14px'
                  }}>
                    {automationResult.success ? (
                      <>
                        <strong>✅ Transfer Successful!</strong>
                        {automationResult.dryRun && <span> (DRY RUN - No actual transfer)</span>}
                        {automationResult.transactionId && (
                          <div style={{ marginTop: '8px', fontSize: '14px' }}>
                            TX: {automationResult.transactionId}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <strong>❌ Transfer Failed</strong>
                        <div style={{ marginTop: '4px' }}>{automationResult.error}</div>
                        {automationResult.errorCode && (
                          <div style={{ marginTop: '4px', fontSize: '14px', opacity: 0.8 }}>
                            Error code: {automationResult.errorCode}
                          </div>
                        )}
                        {automationResult.retryable && (
                          <div style={{ marginTop: '8px', fontSize: '14px', fontStyle: 'italic' }}>
                            ↻ This error may be temporary. You can try again.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  if (modalType === 'approve' || modalType === 'reject') closeProcessingModal();
                  else setShowModal(false);
                }}
                style={{
                  backgroundColor: '#374151',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              {/* v4.8.14 — In-modal Suspend & Reject footer button removed. The
                  destructive fraud-mitigation action is reachable only from the
                  row-level button, which now opens a typed-confirmation modal. */}
              {modalType === 'approve' && (
                <button
                  onClick={handleApprove}
                  disabled={processing || selectedHeldByOther || !auditData?.can_approve || auditedWithdrawalId !== selectedWithdrawal?.id}
                  title={
                    !auditData
                      ? 'Run the Source-of-Funds audit first.'
                      : auditedWithdrawalId !== selectedWithdrawal?.id
                        ? 'Audit verdict is stale — re-run for this withdrawal.'
                        : !auditData.can_approve
                          ? `Approval blocked: requested amount exceeds verified earnings by $${(auditData.totals?.deficit ?? 0).toFixed(2)}.`
                          : 'Approve and pay out this withdrawal.'
                  }
                  style={{
                    backgroundColor: auditData?.can_approve ? '#10B981' : '#374151',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: (processing || !auditData?.can_approve) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: (processing || !auditData?.can_approve) ? 0.5 : 1
                  }}
                >
                  {processing
                    ? 'Processing...'
                    : !auditData
                      ? '🔒 Verify Integrity First'
                      : !auditData.can_approve
                        ? '🚫 Approval Blocked'
                        : '✅ Approve Withdrawal'}
                </button>
              )}
              {modalType === 'reject' && (
                <button
                  onClick={handleReject}
                  disabled={processing || !rejectionReason.trim()}
                  style={{
                    backgroundColor: '#EF4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: (processing || !rejectionReason.trim()) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: (processing || !rejectionReason.trim()) ? 0.7 : 1
                  }}
                >
                  {processing ? 'Processing...' : '❌ Reject Withdrawal'}
                </button>
              )}
              {modalType === 'automate' && (
                <button
                  onClick={handleAutomatedApproval}
                  disabled={processing}
                  style={{
                    backgroundColor: '#8B5CF6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: processing ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: processing ? 0.7 : 1
                  }}
                >
                  {processing ? '🔄 Processing...' : '🤖 Send via Remitano'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* v4.8.14 — Typed-confirmation modal for the destructive Suspend &
          Reject action. Replaces the previous window.prompt flow. Admin must
          enter a reason AND type the username, the withdrawal ID, or the
          exact phrase "SUSPEND AND REJECT" before the POST is sent. Cancel
          and Escape both dismiss the modal without any API call. The
          underlying API contract (operation 'suspend_and_reject_withdrawal')
          is unchanged from v4.8.13. */}
      {fraudConfirmTarget && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: '#1F2937',
            border: '2px solid #EF4444',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '560px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ color: '#EF4444', marginBottom: '12px', fontSize: '20px' }}>
              🚨 Suspend User &amp; Reject Withdrawal
            </h3>
            <div style={{
              backgroundColor: '#7F1D1D33',
              border: '1px solid #7F1D1D',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '16px',
              color: '#FCA5A5',
              fontSize: '13px',
              lineHeight: '1.5'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#FECACA' }}>
                ⚠ Destructive action — please confirm carefully:
              </div>
              <div>1. Sets <code>account_status='suspended'</code> (terminal — user can only see the suspension page).</div>
              <div>2. Rejects this withdrawal and returns funds to escrow.</div>
              <div>3. Writes <code>FRAUD_SUSPEND_ACCOUNT</code> + <code>FRAUD_REJECT_WITHDRAWAL</code> audit rows.</div>
            </div>
            <div style={{ color: '#E5E7EB', fontSize: '14px', marginBottom: '16px', lineHeight: '1.7' }}>
              <div><strong>User:</strong> {fraudConfirmTarget.users?.username || `User ${fraudConfirmTarget.user_id}`}</div>
              <div><strong>Amount:</strong> {fraudConfirmTarget.currency === 'SHARES'
                ? `${fraudConfirmTarget.withdrawal_amount} shares`
                : `$${fraudConfirmTarget.withdrawal_amount}`}</div>
              <div style={{ wordBreak: 'break-all' }}><strong>Withdrawal ID:</strong> {fraudConfirmTarget.id}</div>
            </div>
            <label style={{ display: 'block', color: '#E5E7EB', fontSize: '13px', marginBottom: '4px' }}>
              Reason (required — recorded in the audit log):
            </label>
            <textarea
              value={fraudConfirmReason}
              onChange={(e) => { setFraudConfirmReason(e.target.value); if (fraudConfirmError) setFraudConfirmError(null); }}
              placeholder='e.g. "Inflated/phantom commissions detected"'
              rows={3}
              style={{
                width: '100%', backgroundColor: '#111827', color: '#ffffff',
                border: '1px solid #374151', borderRadius: '4px',
                padding: '8px', fontSize: '14px', marginBottom: '14px',
                resize: 'vertical', fontFamily: 'inherit'
              }}
            />
            <label style={{ display: 'block', color: '#E5E7EB', fontSize: '13px', marginBottom: '4px' }}>
              Type to confirm — one of:&nbsp;
              <code style={{ color: '#F59E0B' }}>{fraudConfirmTarget.users?.username || `(no username — use the ID)`}</code>,&nbsp;
              <code style={{ color: '#F59E0B', wordBreak: 'break-all' }}>{fraudConfirmTarget.id}</code>,&nbsp;
              or the exact phrase&nbsp;
              <code style={{ color: '#F59E0B' }}>SUSPEND AND REJECT</code>:
            </label>
            <input
              type="text"
              value={fraudConfirmTyped}
              onChange={(e) => { setFraudConfirmTyped(e.target.value); if (fraudConfirmError) setFraudConfirmError(null); }}
              placeholder="Type the confirmation phrase…"
              autoComplete="off"
              spellCheck={false}
              style={{
                width: '100%', backgroundColor: '#111827', color: '#ffffff',
                border: '1px solid #374151', borderRadius: '4px',
                padding: '8px', fontSize: '14px', marginBottom: '12px',
                fontFamily: 'monospace'
              }}
            />
            {fraudConfirmError && (
              <div style={{
                backgroundColor: '#7F1D1D33', border: '1px solid #EF4444',
                borderRadius: '4px', padding: '8px 12px', marginBottom: '12px',
                color: '#FCA5A5', fontSize: '13px'
              }}>
                {fraudConfirmError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button
                onClick={closeFraudConfirm}
                disabled={suspendingId === fraudConfirmTarget.id}
                style={{
                  backgroundColor: '#374151', color: '#ffffff', border: 'none',
                  borderRadius: '6px', padding: '8px 16px',
                  cursor: suspendingId === fraudConfirmTarget.id ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: suspendingId === fraudConfirmTarget.id ? 0.7 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={executeSuspendAndReject}
                disabled={suspendingId === fraudConfirmTarget.id || !fraudConfirmReason.trim() || !fraudConfirmTyped.trim()}
                title="Suspend the user and reject this withdrawal. This cannot be undone."
                style={{
                  backgroundColor: '#7F1D1D', color: 'white',
                  border: '1px solid #EF4444', borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: (suspendingId === fraudConfirmTarget.id || !fraudConfirmReason.trim() || !fraudConfirmTyped.trim()) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: (suspendingId === fraudConfirmTarget.id || !fraudConfirmReason.trim() || !fraudConfirmTyped.trim()) ? 0.6 : 1
                }}
              >
                {suspendingId === fraudConfirmTarget.id ? '⏳ Suspending…' : '🚨 Confirm Suspend &amp; Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalManagementDashboard;
